import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Location } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { AppService } from '../../app.service';
import { UtilsService } from '../../shared/utils.service';

/**
 * Filter dialog for the "My Exports" list. Lets the user scope the list by
 * feature, format, status, and requested date range. Mirrors the pattern
 * used by `UserMessagesFilterComponent` so the UX stays consistent across
 * user-scoped list pages.
 */
@Component({
  standalone: false,
  selector: 'app-background-exports-filter',
  templateUrl: './background-exports-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./background-exports-filter.component.scss']
})
export class BackgroundExportsFilterComponent implements OnInit {
  // No API calls are needed; keeps the incoming filter state.
  dataSource = new BackgroundExportsFilterDataSource();

  constructor(
    private app: AppService,
    public location: Location,
    private utils: UtilsService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<BackgroundExportsFilterComponent>
  ) {
    // Set sensible defaults so the dialog renders cleanly even when the
    // caller omits a field.
    if (typeof this.data['filter_feature'] === 'undefined') {
      this.data['filter_feature'] = '';
    }
    if (typeof this.data['filter_format'] === 'undefined') {
      this.data['filter_format'] = '';
    }
    if (typeof this.data['filter_status'] === 'undefined') {
      this.data['filter_status'] = '';
    }
    if (typeof this.data['date_filter_range'] === 'undefined') {
      this.data['date_filter_range'] = [];
    }
  }

  ngOnInit(): void {
    this.dataSource.filter_feature = this.data['filter_feature'];
    this.dataSource.filter_format = this.data['filter_format'];
    this.dataSource.filter_status = this.data['filter_status'];
    this.dataSource.date_filter_range = this.data['date_filter_range'];
  }

  /**
   * Callback from the shared date-range picker.
   */
  getOutputDateRange($event: Date[]): void {
    this.dataSource.date_filter_range = $event;
  }

  /**
   * Clear every filter and close.
   */
  onClearAndClose(): void {
    this.dataSource.filter_feature = '';
    this.dataSource.filter_format = '';
    this.dataSource.filter_status = '';
    this.dataSource.date_filter_range = [];
    this.onSaveAndClose();
  }

  /**
   * Emit the current filter selections back to the caller and close.
   */
  onSaveAndClose(): void {
    this.dialogRef.close({
      filter_feature: this.dataSource.filter_feature,
      filter_format: this.dataSource.filter_format,
      filter_status: this.dataSource.filter_status,
      date_filter_range: this.dataSource.date_filter_range
    });
  }

  /**
   * Clear only the date range without closing the dialog.
   */
  onClearDateRange(): void {
    this.dataSource.date_filter_range = [];
  }
}

/**
 * Local dialog state. No API calls; just holds the current filter values so
 * the template can two-way-bind against them.
 */
export class BackgroundExportsFilterDataSource {
  filter_feature: string = '';
  filter_format: string = '';
  filter_status: string = '';
  date_filter_range: Date[] = [];
}
